import Socket from '../connection/socket';
import * as InvitationActions from '../stores/actions/invitations';
import * as SessionActions from '../stores/actions/session';
import * as ContentActions from '../stores/actions/content';
import * as HomeActions from '../stores/actions/home';
import * as ShotsActions from '../stores/actions/shots';
import * as Alerts from '../components/general-components/alert-service';
import { Invitation } from '../stores/reducers/invitations';
import { getState, socketListenerDispatch } from './socket-listener-main';
import { InviteAccepted, LoadInvitations, LoadMeeting } from '../connection/meeting';
import { GreenroomState } from '../stores/types/types';
import { getUsersName } from '../utils/utils';

export function joinRequest(socketMessage: any) {
	const {
		InvitationsReducer: {
			invitations,
			joinRequests,
		},
	} = getState();

	const user = invitations.find(
		(invite: Invitation) => (
			invite.profile.email.toLowerCase() === socketMessage.message.email.toLowerCase()
		),
	);

	if (user) {
		if (joinRequests.some((request: Invitation) => request.code === user.code)) {
			socketListenerDispatch(InvitationActions.updateJoinRequests(socketMessage.message));
			return;
		}
		socketListenerDispatch(
			InvitationActions.addJoinRequests({
				...user,
				join_request: socketMessage.message.join_request,
			}),
		);
	}
}

export function inviteStatusChanged() {
	const {
		SessionReducer: {
			token,
			meeting,
		},
		HomeReducer: {
			homeMeeting,
		},
	} = getState();

	// don't bother doing this if we are already in the meeting
	if (!meeting) { socketListenerDispatch(ContentActions.loadMeetingsPresenter(token)); }

	// reload for rehearsal invite status in show details
	if (homeMeeting) {
		socketListenerDispatch(SessionActions.getShowSessions(token, homeMeeting.meeting));
	}
}

export async function joinApproved(message: any) {
	const {
		SessionReducer: {
			user,
			selectedEvent,
			token,
			meeting,
			host,
		},
	} = getState();

	if (host && message?.message?.email) {
		socketListenerDispatch(InvitationActions.removeJoinRequestByEmail(message.message.email, true));
	}

	if (message.message.email.toLowerCase().trim() === user.email.toLowerCase().trim()) {
		if (meeting) {
			window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
			socketListenerDispatch(InvitationActions.loadInvitations(token, meeting.meeting));
			socketListenerDispatch(SessionActions.setOnboardingComplete(true));
			InviteAccepted(token, meeting.meeting).catch((e) => console.error(e));
		} else {
			// eslint-disable-next-line no-underscore-dangle
			const _meeting = await LoadMeeting(token, selectedEvent.meeting);
			socketListenerDispatch(SessionActions.setSelectedMeeting(_meeting));
			window.history.pushState(undefined, 'Greenroom', `/${_meeting.meeting_id}`);
			socketListenerDispatch(InvitationActions.loadInvitations(token, _meeting.meeting));
			socketListenerDispatch(SessionActions.setOnboardingComplete(true));
			InviteAccepted(token, _meeting.meeting).catch((e) => console.error(e));
		}
	}
}

export async function joinRejected(message: any) {
	const {
		SessionReducer: {
			user,
			host,
		},
	} = getState();

	if (host && message?.message?.email) {
		socketListenerDispatch(InvitationActions.removeJoinRequestByEmail(message.message.email, true));
	}

	if (message.message.email.toLowerCase().trim() === user.email.toLowerCase().trim()) {
		socketListenerDispatch(InvitationActions.setJoinRequest(null));
		Alerts.showAlert({
			message: 'Invitation Rejected',
			description: 'The Producer has rejected your request to join the meeting.',
			duration: 5000,
			type: 'error',
		});
	}
}

export function updateMeetingPresenter(message: any) {
	const {
		ContentReducer: {
			events,
		},
		HomeReducer: {
			homeMeeting,
		},
	} = getState();

	const newEvents = events.map((event: any) => {
		if (event.meeting === message.message.meeting) {
			// eslint-disable-next-line no-param-reassign
			event.entry_without_producer = message.message.entry_without_producer;
		}
		return event;
	});

	socketListenerDispatch(ContentActions.updateMeetingsPresenter(newEvents));

	if (homeMeeting?.meeting === message.message.meeting) {
		socketListenerDispatch(HomeActions.setHomeMeeting({
			...homeMeeting,
			entry_without_producer: message.message.entry_without_producer,
		}));
	}
}

export function handleUserEnteringRoom(message: any) {
	const {
		SessionReducer: {
			session,
			user,
			connectedUsers,
		},
	} = getState();

	// eslint-disable-next-line camelcase
	const { joining, watcher_only, from } = message.message;
	
	const connectedPresenters = connectedUsers.filter((u: any) => !u.watcher_only && u.in_room);	
	let numConnectedPresenters = connectedPresenters.length + 1; //the joining presenter does not yet have in_room set, so we add one user
	const presentercapMaxed = numConnectedPresenters >= 20;

	if (from !== session && joining.gr_user !== user.gr_user) {
		Alerts.showAlert({
			// eslint-disable-next-line camelcase
			message: `New ${watcher_only ? 'Viewer' : 'Presenter'}`,
			description: `${getUsersName(joining)} has joined the room.`,
			duration: 2000,
			type: 'neutral',
		});
	}

	if (presentercapMaxed && !watcher_only) {
		setTimeout(() => {
			Alerts.showAlert({
				message: 'Too many active participants',
				description: 'Over 20 users may cause performance issues',
				type: 'presenter-cap',
				duration: 8000,
				autoWidth: true,
			});
		}
		, 2000);
	} 
}

function handleInvitesUpdate(message: any) {
	const {
		InvitationsReducer: { invitations },
	} = getState() as GreenroomState;
	// if the invitations are different, only then do we update
	if (JSON.stringify(invitations) !== JSON.stringify(message.message)) {
		socketListenerDispatch(InvitationActions.updateInvitations(message.message));
	}
}

async function handleMeetingInvitesUpdated(message: any) {
	const {
		SessionReducer: {
			meeting,
			token,
		},
	} = getState();

	const newInvs = await LoadInvitations(token, meeting.meeting);

	if (message.message?.meeting === meeting.meeting) {
		socketListenerDispatch(InvitationActions.updateInvitations(newInvs));

		if (message.message.updateShots) {
			socketListenerDispatch(ShotsActions.loadShots(token, meeting.meeting));
		}
	}
}

export default function Invitations(socket: Socket) {
	// initial state = nothing here should change at all
	// during the session - like the host or the meeting
	const {
		SessionReducer: {
			host,
		},
	} = getState();

	socket.addListener('invites-update', handleInvitesUpdate);
	socket.addListener('meeting-invites-updated', handleMeetingInvitesUpdated);

	if (host) {
		socket.addListener('presenter-join-request', joinRequest);
		socket.addListener('user-entered-room', handleUserEnteringRoom);
	} else {
		socket.addListener('new-invite', inviteStatusChanged);
		socket.addListener('revoke-invite', inviteStatusChanged);
		socket.addListener('update-entry', updateMeetingPresenter);
	}
	socket.addListener('approve-join-request', joinApproved);
	socket.addListener('reject-join-request', joinRejected);
}
